import React, { useState, useCallback } from "react";
import { WEDDING } from "../utils/config";
import { validateRSVP, hasErrors } from "../utils/validation";

const INIT = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  events: "",
  guestCount: "0",
  notes: "",
};

const EVENTS_OPTS = [
  "RSVP-Evening wedding celebration 19 September 2026",
  "Regretfully unable to attend",
];

// ── Apps Script Web App URL ───────────────────────────────
const APPS_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwTDPCfNEUE516w34ZymCEfxY7jFdm_y88-aN1cBbCzfeGuIVpjAV9JnQMB6Yx1TSTL/exec";

function Field({ label, error, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      <label className="form-label">
        {label}
        {required && (
          <span style={{ color: "#C0392B", marginLeft: "3px" }}>*</span>
        )}
      </label>
      {children}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export default function RSVP() {
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback(
    (field) => (e) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
    },
    [],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate first
    const errs = validateRSVP(form);
    if (hasErrors(errs)) {
      setErrors(errs);
      setLoading(false);
      return;
    }

    try {
      // Send directly to Google Apps Script
      // Apps Script receives the JSON and writes a row to your Google Sheet
      await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors", // required for Apps Script cross-origin requests
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          events: form.events,
          guestCount: form.guestCount,
          notes: form.notes,
        }),
      });

      // no-cors means we can't read the response
      // but Apps Script still receives and processes the data
      setSent(true);
      setForm(INIT);
    } catch (error) {
      console.error("RSVP Error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fi = {
    padding: "12px 15px",
    background: "#fff",
    border: ".5px solid var(--sage-p)",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 300,
    color: "var(--tx)",
    outline: "none",
    width: "100%",
    transition: "border-color .2s",
  };

  return (
    <section
      id="rsvp"
      aria-labelledby="rsvp-h"
      style={{ background: "var(--mint-p)", padding: "96px 36px" }}
    >
      <div style={{ maxWidth: "620px", margin: "0 auto" }} className="reveal">
        <div style={{ textAlign: "center", marginBottom: "44px" }}>
          <div
            className="section-tag"
            style={{ justifyContent: "center", display: "flex" }}
          >
            Kindly Respond By {WEDDING.rsvpDeadline}
          </div>
          <h2 id="rsvp-h" className="section-title">
            Will you <em>join us?</em>
          </h2>
          <p
            style={{
              fontSize: "13.5px",
              lineHeight: 1.9,
              color: "var(--muted)",
              marginTop: "10px",
            }}
          >
            Your RSVP is saved directly to our guest list. We will be in touch
            to confirm.
          </p>
        </div>

        {sent ? (
          <div
            role="status"
            style={{
              textAlign: "center",
              padding: "44px",
              background: "#fff",
              borderRadius: "12px",
              border: ".5px solid var(--sage-p)",
            }}
          >
            <div style={{ fontSize: "34px", marginBottom: "16px" }}>🌿</div>
            <h3
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: "30px",
                color: "var(--forest)",
                marginBottom: "9px",
              }}
            >
              Thank you!
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "var(--muted)",
                lineHeight: 1.8,
              }}
            >
              Your RSVP has been saved to our guest list. We cannot wait to
              celebrate with you! 🌿
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            {/* Name row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
              }}
            >
              <Field label="First Name" error={errors.firstName} required>
                <input
                  style={fi}
                  type="text"
                  placeholder="Jane"
                  value={form.firstName}
                  onChange={handleChange("firstName")}
                  maxLength={50}
                  autoComplete="given-name"
                />
              </Field>
              <Field label="Surname" error={errors.lastName} required>
                <input
                  style={fi}
                  type="text"
                  placeholder="Smith"
                  value={form.lastName}
                  onChange={handleChange("lastName")}
                  maxLength={50}
                  autoComplete="family-name"
                />
              </Field>
            </div>

            {/* Email */}
            <Field label="Email Address" error={errors.email} required>
              <input
                style={fi}
                type="email"
                placeholder="jane@email.com"
                value={form.email}
                onChange={handleChange("email")}
                maxLength={100}
                autoComplete="email"
              />
            </Field>

            {/* Phone */}
            <Field label="Phone Number">
              <input
                style={fi}
                type="tel"
                placeholder="+267 7X XXX XXXX"
                value={form.phone}
                onChange={handleChange("phone")}
                maxLength={20}
                autoComplete="tel"
              />
            </Field>

            {/* Attending */}
            <Field label="Are you attending?" error={errors.events} required>
              <select
                style={fi}
                value={form.events}
                onChange={handleChange("events")}
              >
                <option value="">Please select…</option>
                {EVENTS_OPTS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>

            {/* Song request */}
            <Field label="Song request?">
              <textarea
                style={{ ...fi, resize: "none" }}
                rows={3}
                placeholder="Any song you'd love to hear on the night…"
                value={form.notes}
                onChange={handleChange("notes")}
                maxLength={500}
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                alignSelf: "center",
                marginTop: "6px",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? "Sending…" : "Send My RSVP →"}
            </button>

            <p
              style={{
                textAlign: "center",
                fontSize: "10px",
                color: "var(--muted)",
                letterSpacing: ".06em",
              }}
            >
              Responses saved directly to our guest list · M &amp; O 2026
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
